import React from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssetInformationStep from './AssetInformationStep';
import { AddAssetProvider, useAddAsset } from '../../../context/AddAssetContext';
import RoleBasedAccess from '../../../services/RoleBasedAccess';

interface AddAssetModalContentProps {
  onClose: () => void;
}

const AddAssetModalContent: React.FC<AddAssetModalContentProps> = ({ onClose }) => {
  const theme = useTheme();

  const {
    activeStep,
    handleNext,
    handleBack,
    handleRunVehicle,
    assetId,
    selectedSystemCode,
    selectedConfigName,
    selectedConfiguration,
    noConfigsFound,
    steps
  } = useAddAsset();

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <AssetInformationStep />
        );
      case 1:
        return <Typography sx={{ mt: 2 }}>Step 2: Upload Images (Placeholder)</Typography>;
      case 2:
        return <Typography sx={{ mt: 2 }}>Step 3: Confirm & Save (Placeholder)</Typography>;
      default:
        return <Typography sx={{ mt: 2 }}>Unknown Step</Typography>;
    }
  };

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '800px',
    bgcolor: 'background.paper',
    border: `2px solid ${theme.palette.divider}`,
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
  };

  const canRunVehicle = assetId && selectedConfiguration?.clientProcess?.id

  return (
    <Box sx={style}>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <Typography id="add-asset-modal-title" variant="h6" component="h2">
        Add New Asset
      </Typography>

      {activeStep > 0 && assetId && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
          AssetId: {assetId}
        </Typography>
      )}

      <Stepper
        activeStep={activeStep}
        sx={{
          mt: 3,
          mb: 2,
          '& .MuiStepIcon-root.Mui-active': {
            color: 'error.main',
          },
          '& .MuiStepIcon-root.Mui-completed': {
            color: 'error.main',
          },
        }}
      >
        {(steps || []).map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 3 }}>
        <Button variant="outlined" color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
          Back
        </Button>

        <Button
          variant="outlined"
          color="inherit" 
          onClick={handleRunVehicle}
          disabled={!canRunVehicle}
        >
          Run Vehicle on Odroid
        </Button>

        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          variant="contained"
          color="error"
          onClick={handleNext}
          disabled={!assetId || !selectedSystemCode || !selectedConfigName || noConfigsFound}
        >
          {activeStep === steps.length - 1 ? 'Save' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

interface AddAssetModalProps {
  open: boolean;
  onClose: () => void;
}

const AddAssetModal: React.FC<AddAssetModalProps> = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="add-asset-modal-title">
      <AddAssetProvider onClose={onClose}>
        <AddAssetModalContent onClose={onClose} />
      </AddAssetProvider>
    </Modal>
  );
};

export default AddAssetModal;